import { ErrorHandler, inject, Injectable, Injector, NgZone } from '@angular/core'
import { ExceptionHelperService } from './exception-helper.service'
import { ExceptionSeverity } from './exception-severity.enum'
import { environment } from '../../../environments/environment'
import { PrimeNgToastService } from '../prime-ng-toast/prime-ng-toast.service'

const UNEXPECTED_ERROR_KEY = 'TOAST.UNEXPECTED_ERROR'
const TOAST_DURATION_MS = 6000

/** A failing template expression re-throws on every change detection cycle; without this the screen fills with toasts. */
const DUPLICATE_SUPPRESSION_MS = 5000

type ErrorLike = { name?: string; message?: string }

@Injectable()
export class GlobalErrorHandlerService implements ErrorHandler {
  private exceptionHelper = inject(ExceptionHelperService)
  // Resolved on demand: the ErrorHandler is constructed before the rest of the injector, and the toast service pulls in Transloco.
  private injector = inject(Injector)
  private zone = inject(NgZone)

  private lastErrorMessage: string | null = null
  private lastErrorAt = 0

  handleError(error: any): void {
    if (this.exceptionHelper.isSyncfusionFirstChildError(error)) {
      return
    }
    // Add breadcrumb for the error
    this.exceptionHelper.addBreadcrumb('Unhandled Error', 'error', { error: error.message || 'Unknown error' })

    // Capture the error with default severity
    this.exceptionHelper.captureException(error, ExceptionSeverity.ERROR, {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    })

    this.notifyUser(error)

    // Log to console in development
    if (!environment.production) {
      console.error('Global error handler caught an error:', error)
    }
  }

  private notifyUser(error: unknown): void {
    const { name, message } = (error ?? {}) as ErrorLike

    // The Feathers client error hook already toasts this one and redirects to the login page.
    if (name === 'NotAuthenticated') {
      return
    }
    if (this.isDuplicate(message ?? String(error))) {
      return
    }

    try {
      // p-toast only calls markForCheck() on a new message, so a toast queued outside the zone never renders.
      // Errors reach the ErrorHandler from wherever they were thrown, which is not necessarily inside it.
      this.zone.run(() =>
        this.injector
          .get(PrimeNgToastService)
          .displayCustomToast('error', UNEXPECTED_ERROR_KEY, { durationMs: TOAST_DURATION_MS }),
      )
    } catch (toastError) {
      // Rethrowing would re-enter handleError; the original error was already captured above.
      console.error('Global error handler could not display a toast:', toastError)
    }
  }

  /** Each occurrence slides the window, so a sustained loop produces one toast rather than one per quiet period. */
  private isDuplicate(message: string): boolean {
    const now = Date.now()
    const isRepeat = message === this.lastErrorMessage && now - this.lastErrorAt < DUPLICATE_SUPPRESSION_MS

    this.lastErrorMessage = message
    this.lastErrorAt = now

    return isRepeat
  }
}
